<template>
    <div class="bg-white rounded p-5">
        <h3 class="mb-4" style="color: #1a0054">
            <strong>Crea tus rutas</strong>
        </h3>

        <!-- Selected Courses Section -->
        <div class="row">
            <div class="bg-grey rounded-8 my-4 p-4">
                <label><b>Cursos seleccionados:</b></label>
                <div v-if="!hasSelectedCourses" class="col-12 text-center">
                    <small
                        >Aquí podrás ver los cursos que selecciones para la ruta
                        {{ info.name }}</small
                    >
                </div>
                <div class="mt-3" v-else>
                    <div
                        v-for="course in selectedCourses"
                        :key="course.id"
                        class="col-lg-4 col-md-4 col-sm-12 col-xs-12 p-2 d-inline-block w-100"
                    >
                        <div class="bg-white rounded-8 p-3">
                            <div
                                class="d-flex justify-content-between align-items-center"
                            >
                                <p class="mb-0">
                                    <b>{{ course.name }}</b>
                                </p>
                                <button
                                    class="btn btn-sm text-danger"
                                    @click="handleRemoveCourse(course)"
                                >
                                    <i class="fa-solid fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="hasSelectedCourses" class="col-12 text-end">
                <button
                    class="btn bg-utel-v2-secondary sin-borde text-primary rounded-8 mx-1 my-sm-2 pt-2"
                    @click="handleBack"
                >
                    Regresar
                </button>
                <button
                    class="btn bg-utel text-white sin-borde rounded-8 px-5"
                    @click="handleContinue"
                >
                    Continuar
                </button>
            </div>
        </div>

        <!-- Filters Section -->
        <h5 class="my-4">
            <strong>Selecciona una categoría para los cursos de la ruta</strong>
        </h5>

        <div class="row">
            <div class="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                <label><b>Categorías</b></label>
                <select
                    class="form-control rounded-8 sin-borde"
                    v-model="filters.category"
                >
                    <option value="">Seleccione una categoría</option>
                    <optgroup
                        v-for="(cat, index) of categories.children"
                        :key="index"
                        :label="cat.name"
                    >
                        <option
                            v-for="(catc, i) of cat.children"
                            :key="'child' + i"
                            :value="catc.term_id"
                        >
                            {{ catc.name }}
                        </option>
                    </optgroup>
                </select>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                <label><b>Tipo</b></label>
                <select
                    class="form-control rounded-8 sin-borde"
                    v-model="filters.courseType"
                >
                    <option value="default">Cursos Utel</option>
                    <option value="propios">Cursos Propios</option>
                </select>
            </div>
        </div>
        <div class="row">
            <div class="d-flex justify-content-end">
                <search-input-component
                    :search="filters.search"
                    @onSearch="handleSearchChange"
                ></search-input-component>
                <button
                    class="btn bg-utel text-white sin-borde rounded-8 mx-1 my-sm-1"
                    @click="handleFilterCourses"
                >
                    Filtrar cursos
                </button>
            </div>
        </div>

        <!-- Courses Grid -->
        <div class="row" ref="coursesSection">
            <h4 class="mt-4"><strong>Seleccionar cursos</strong></h4>
        </div>

        <div class="row" v-show="!loading">
            <course-card
                v-for="course in courses"
                :key="course.id"
                :course="course"
                @view-info="handleViewCourseInfo"
                @add-course="handleAddCourse"
            />

            <course-pagination
                :current-page="currentPage"
                :last-page="lastPage"
                :show-pagination="showPagination"
                :show-first-page="showFirstPage"
                :show-last-page="showLastPage"
                :has-next-page="hasNextPage"
                :has-prev-page="hasPrevPage"
                @go-to-page="handleGoToPage"
                @next-page="handleNextPage"
                @prev-page="handlePrevPage"
            />
        </div>

        <loading-component v-show="loading"></loading-component>

        <div class="col-12 text-end">
            <button
                class="btn bg-utel-v2-secondary sin-borde text-primary rounded-8 mx-1 my-sm-2 pt-2"
                @click="handleBack"
            >
                Regresar
            </button>
            <button
                class="btn bg-utel text-white sin-borde rounded-8 px-5"
                @click="handleContinue"
            >
                Continuar
            </button>
        </div>

        <ModalInfoCourse
            v-if="showModalInfoCourse"
            :course="watchCourse"
            @setCourse="handleSetCourse"
            @close="closeModalInfoCourse"
        />
    </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance } from "vue";
import ModalInfoCourse from "../modals/ModalInfoCourse.vue";
import CourseCard from "../CourseCard.vue";
import CoursePagination from "../CoursePagination.vue";
import { useCoursePagination } from "../../../composables/useCoursePagination.js";
import { useCourseFilters } from "../../../composables/useCourseFilters.js";
import { useCourseSelection } from "../../../composables/useCourseSelection.js";
import { useCoursesApi } from "../../../composables/useCoursesApi.js";

export default {
    name: "CreateCustomRouteCourses",

    components: {
        ModalInfoCourse,
        CourseCard,
        CoursePagination,
    },

    props: {
        info: { type: Object, required: true },
        url_courses_wp: { type: String, required: true },
        uuidclient: { type: String },
    },

    emits: ["setRuta", "changeStep"],

    setup(props, { emit }) {
        const instance = getCurrentInstance();
        const coursesSection = ref(null);

        // Composables
        const {
            currentPage,
            lastPage,
            showPagination,
            hasNextPage,
            hasPrevPage,
            showFirstPage,
            showLastPage,
            goToPage,
            resetPagination,
            updatePagination,
        } = useCoursePagination();

        const {
            filters,
            categories,
            parentCategories,
            buildFilterParams,
            loadCategories,
        } = useCourseFilters();

        const {
            selectedCourses,
            parentId,
            hasSelectedCourses,
            addCourse,
            removeCourse,
            initializeFromLocalStorage,
        } = useCourseSelection();

        const { courses, loading, fetchCourses, fetchCoursesByPage } =
            useCoursesApi(props.url_courses_wp);

        // Local state
        const route = ref({
            name: "",
            description: "",
            courses: [],
            students: [],
        });

        const showModalInfoCourse = ref(false);
        const watchCourse = ref({
            id: "",
            content: "",
            name: "",
            duration: "",
            sections: [],
            count_students: 0,
        });

        // Methods
        const handleContinue = async () => {
            if (!hasSelectedCourses.value) {
                await instance.appContext.config.globalProperties.$swal({
                    icon: 'warning',
                    title: 'Atención',
                    text: 'Debe seleccionar al menos un curso',
                    confirmButtonText: 'Aceptar'
                });
                return;
            }

            route.value.courses = selectedCourses.value;
            route.value.parentId = parentId.value;
            emit("setRuta", route.value);
            emit("changeStep", 3);
        };

        const handleBack = () => {
            emit("setRuta", route.value);
            emit("changeStep", 1);
        };

        const handleRemoveCourse = (course) => {
            removeCourse(course);
        };

        const handleAddCourse = (course) => {
            addCourse(course, parentCategories.value);
        };

        const handleViewCourseInfo = (course) => {
            watchCourse.value = course;
            showModalInfoCourse.value = true;
        };

        const handleSetCourse = (course) => {
            handleAddCourse(course);
            showModalInfoCourse.value = false;
        };

        const closeModalInfoCourse = () => {
            showModalInfoCourse.value = false;
        };

        const handleSearchChange = (searchText) => {
            filters.search = searchText;
        };

        const handleFilterCourses = async () => {
            resetPagination();
            const params = buildFilterParams({ uuidClient: props.uuidclient });
            const data = await fetchCourses(params);
            updatePagination(data);
        };

        const handleGoToPage = async (page) => {
            coursesSection.value?.scrollIntoView({ behavior: 'smooth' });

            goToPage(page);
            const params = buildFilterParams({
                uuidClient: props.uuidclient,
                page: page,
            });
            const data = await fetchCoursesByPage(page, params);
            updatePagination(data);
        };

        const handleNextPage = () => {
            if (hasNextPage.value) {
                handleGoToPage(currentPage.value + 1);
            }
        };

        const handlePrevPage = () => {
            if (hasPrevPage.value) {
                handleGoToPage(currentPage.value - 1);
            }
        };

        const initializeComponent = async () => {
            // Initialize route data
            if (props.info.name) {
                route.value = { ...props.info };
            }

            // Initialize selected courses
            selectedCourses.value = props.info.courses || [];
            parentId.value = props.info.parentId || 0;

            // Load categories and courses
            await loadCategories();
            await handleFilterCourses();

            // Handle localStorage courses
            const urlParams = new URLSearchParams(window.location.search);
            if (!urlParams.get("rutas")) {
                localStorage.removeItem("cursoDefecto");
            }
            initializeFromLocalStorage();
        };

        onMounted(() => {
            initializeComponent();
        });

        return {
            // Refs
            coursesSection,
            // Reactive data
            route,
            filters,
            categories,
            courses,
            loading,
            selectedCourses,
            hasSelectedCourses,
            showModalInfoCourse,
            watchCourse,

            // Pagination
            currentPage,
            lastPage,
            showPagination,
            hasNextPage,
            hasPrevPage,
            showFirstPage,
            showLastPage,

            // Methods
            handleContinue,
            handleBack,
            handleRemoveCourse,
            handleAddCourse,
            handleViewCourseInfo,
            handleSetCourse,
            closeModalInfoCourse,
            handleSearchChange,
            handleFilterCourses,
            handleGoToPage,
            handleNextPage,
            handlePrevPage,
        };
    },
};
</script>

<style scoped>
.bg-grey {
    background-color: #cbb9ff66;
}
</style>
